/**
 * 📊 DASHBOARD DBA SIMPLE
 * 
 * Componente React simple para dashboard de administración DBA
 * Compatible con Material-UI
 * 
 * @author Desarrollador Full Stack DBA
 * @version 1.0.0
 */

import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Switch,
  FormControlLabel,
  Divider,
  CircularProgress,
  Badge,
  IconButton
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  Storage as StorageIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
  Message as MessageIcon
} from "@material-ui/icons";
import api from "../services/api";

const DBADashboard = () => {
  const isMounted = useRef(true); // Referencia para rastrear si el componente está montado
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalContacts: 0,
    duplicateContacts: 0,
    totalTickets: 0,
    duplicateTickets: 0,
    lidOperations: 0,
    avgProcessingTime: 0,
    systemStatus: 'active'
  });
  const [duplicates, setDuplicates] = useState([]);
  const [lidMetrics, setLidMetrics] = useState({
    totalOperations: 0,
    successfulOperations: 0,
    failedOperations: 0,
    avgProcessingTime: 0
  });
  const [debugConfig, setDebugConfig] = useState({
    enableLidDebug: false,
    enableDuplicateDetection: false,
    enablePerformanceMonitoring: false
  });

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000); // Actualizar cada 30 segundos
    
    return () => {
      isMounted.current = false; // Marcar como desmontado
      clearInterval(interval);
    };
  }, []);

  const fetchDashboardData = async () => {
    try {
      if (!isMounted.current) return; // No actualizar si el componente está desmontado
      setLoading(true);
      
      // Fetch status
      const statsResponse = await api.get('/monitoring/status');
      if (!isMounted.current) return; // Verificar después de cada operación asíncrona
      const statsData = statsResponse.data;
      setStats({
        totalContacts: statsData?.stats?.duplicateContacts?.length || 0,
        duplicateContacts: statsData?.stats?.duplicateContacts?.length || 0,
        totalTickets: statsData?.stats?.duplicateTickets?.length || 0,
        duplicateTickets: statsData?.stats?.duplicateTickets?.length || 0,
        lidOperations: 0,
        avgProcessingTime: 0,
        systemStatus: statsData?.status === 'healthy' ? 'active' : (statsData?.status === 'warning' ? 'warning' : 'inactive')
      });

      // Set duplicates from stats
      if (!isMounted.current) return;
      setDuplicates(statsData?.stats?.duplicateContacts || []);

      // Set LID metrics (placeholder)
      if (!isMounted.current) return;
      setLidMetrics({
        totalOperations: 0,
        successfulOperations: 0,
        failedOperations: 0,
        avgProcessingTime: 0
      });

      // Get debug config from services in response
      if (!isMounted.current) return;
      setDebugConfig({
        enableLidDebug: statsData?.services?.debugLid || false,
        enableDuplicateDetection: statsData?.services?.duplicateDetection || false,
        enablePerformanceMonitoring: statsData?.services?.performanceMonitoring || false
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (!isMounted.current) return;
      // Mantener valores por defecto en caso de error
    } finally {
      if (!isMounted.current) return;
      setLoading(false);
    }
  };

  const handleToggleService = async (service, enabled) => {
    try {
      if (!isMounted.current) return;
      // Mapear nombres de servicio de frontend a backend
      const serviceMap = {
        'enableLidDebug': 'debugLid',
        'enableDuplicateDetection': 'duplicateDetection',
        'enablePerformanceMonitoring': 'performanceMonitoring'
      };
      const backendService = serviceMap[service] || service;
      
      // Actualizar estado local inmediatamente para mejor UX
      setDebugConfig(prev => ({ ...prev, [service]: enabled }));
      
      await api.post('/monitoring/toggle-service', { service: backendService, enabled });
      
      if (!isMounted.current) return;
      console.log(`✅ Servicio ${service} ${enabled ? 'activado' : 'desactivado'} exitosamente`);
    } catch (error) {
      console.error('Error toggling service:', error);
      if (!isMounted.current) return;
      // Revertir cambio en caso de error
      setDebugConfig(prev => ({ ...prev, [service]: !enabled }));
    }
  };

  const handleCleanupDuplicates = async () => {
    try {
      if (!isMounted.current) return;
      await api.post('/monitoring/cleanup');
      if (!isMounted.current) return;
      fetchDashboardData();
    } catch (error) {
      console.error('Error cleaning up duplicates:', error);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
        <Typography variant="h6" style={{ marginLeft: 16 }}>
          Cargando Dashboard DBA...
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        <span role="img" aria-label="Dashboard">📊</span> Dashboard DBA - Monitoreo de Duplicados
      </Typography>
      
      <Grid container spacing={3}>
        {/* Estadísticas principales */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <PeopleIcon color="primary" style={{ marginRight: 8 }} />
                <Typography variant="h6">Contactos</Typography>
              </Box>
              <Typography variant="h4">{stats.totalContacts}</Typography>
              <Typography color="textSecondary">
                Duplicados: {stats.duplicateContacts}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={(stats.duplicateContacts / stats.totalContacts) * 100} 
                style={{ marginTop: 8 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <MessageIcon color="primary" style={{ marginRight: 8 }} />
                <Typography variant="h6">Tickets</Typography>
              </Box>
              <Typography variant="h4">{stats.totalTickets}</Typography>
              <Typography color="textSecondary">
                Duplicados: {stats.duplicateTickets}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={(stats.duplicateTickets / stats.totalTickets) * 100} 
                style={{ marginTop: 8 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <SpeedIcon color="primary" style={{ marginRight: 8 }} />
                <Typography variant="h6">Operaciones LID</Typography>
              </Box>
              <Typography variant="h4">{lidMetrics.totalOperations}</Typography>
              <Typography color="textSecondary">
                Tiempo promedio: {lidMetrics.avgProcessingTime}ms
              </Typography>
              <Chip 
                label={lidMetrics.totalOperations === 0 ? "⚪ Sin datos" : (lidMetrics.successfulOperations > lidMetrics.failedOperations ? "🟢 Estable" : "🔴 Inestable")}
                color={lidMetrics.totalOperations === 0 ? "default" : (lidMetrics.successfulOperations > lidMetrics.failedOperations ? "primary" : "secondary")}
                size="small"
                style={{ marginTop: 8 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <CheckCircleIcon color="primary" style={{ marginRight: 8 }} />
                <Typography variant="h6">Estado del Sistema</Typography>
              </Box>
              <Typography variant="h4">
                {stats.systemStatus === 'active' ? 
                  <><span role="img" aria-label="Activo">🟢</span> Activo</> : 
                  stats.systemStatus === 'warning' ?
                  <><span role="img" aria-label="Advertencia">⚠️</span> Advertencia</> :
                  <><span role="img" aria-label="Inactivo">🔴</span> Inactivo</>}
              </Typography>
              <Typography color="textSecondary">
                Última actualización: {new Date().toLocaleTimeString()}
              </Typography>
              <IconButton onClick={fetchDashboardData} size="small">
                <RefreshIcon />
              </IconButton>
            </CardContent>
          </Card>
        </Grid>

        {/* Configuración de servicios */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <span role="img" aria-label="Configuración">⚙️</span> Configuración de Servicios
              </Typography>
              <Divider style={{ marginBottom: 16 }} />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={debugConfig.enableLidDebug}
                    onChange={(e) => handleToggleService('enableLidDebug', e.target.checked)}
                  />
                }
                label="Debug LID"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={debugConfig.enableDuplicateDetection}
                    onChange={(e) => handleToggleService('enableDuplicateDetection', e.target.checked)}
                  />
                }
                label="Detección de Duplicados"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={debugConfig.enablePerformanceMonitoring}
                    onChange={(e) => handleToggleService('enablePerformanceMonitoring', e.target.checked)}
                  />
                }
                label="Monitoreo de Rendimiento"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Acciones */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <span role="img" aria-label="Herramientas">🛠️</span> Acciones DBA
              </Typography>
              <Divider style={{ marginBottom: 16 }} />
              
              <Button
                variant="contained"
                color="primary"
                onClick={fetchDashboardData}
                startIcon={<RefreshIcon />}
                style={{ marginRight: 8, marginBottom: 8 }}
              >
                Actualizar Datos
              </Button>
              
              <Button
                variant="contained"
                color="secondary"
                onClick={handleCleanupDuplicates}
                startIcon={<StorageIcon />}
                style={{ marginBottom: 8 }}
              >
                Limpiar Duplicados
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Tabla de duplicados */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <span role="img" aria-label="Búsqueda">🔍</span> Duplicados Detectados
              </Typography>
              <Divider style={{ marginBottom: 16 }} />
              
              {duplicates.length > 0 ? (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID Contacto</TableCell>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Número</TableCell>
                      <TableCell>Tickets Duplicados</TableCell>
                      <TableCell>Prioridad</TableCell>
                      <TableCell>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {duplicates.slice(0, 10).map((duplicate) => (
                      <TableRow key={duplicate.contactId}>
                        <TableCell>{duplicate.contactId}</TableCell>
                        <TableCell>{duplicate.name}</TableCell>
                        <TableCell>{duplicate.number}</TableCell>
                        <TableCell>
                          <Badge badgeContent={duplicate.ticketCount} color="error">
                            <MessageIcon />
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={duplicate.priority}
                            color={duplicate.priority === 'high' ? 'secondary' : 'primary'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Button size="small" color="primary">
                            Consolidar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Alert severity="success">
                  <span role="img" aria-label="Éxito">✅</span> No se encontraron duplicados. El sistema está funcionando correctamente.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DBADashboard;